type GtagFunction = (...args: any[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GtagFunction;
  }
}

const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;

// Admin exclusion: we intentionally use a Vite-exposed env var so the client
// can suppress analytics for the admin user.
//
// IMPORTANT: `ADMIN_USER_ID` is server-only in this repo; for client-side usage
// set `VITE_ADMIN_USER_ID` in your Vercel env vars / .env.local.
const adminUserId = import.meta.env.VITE_ADMIN_USER_ID as string | undefined;

let initialized = false;

function ensureGtagStub(): void {
  window.dataLayer = window.dataLayer || [];
  // Classic GA snippet: gtag() pushes into dataLayer.
  function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments as any);
  }
  window.gtag = window.gtag || (gtag as any);
}

function injectGtagScript(id: string): void {
  // Avoid double-inject.
  if (document.querySelector(`script[data-ga4="${id}"]`)) return;

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  s.setAttribute('data-ga4', id);
  document.head.appendChild(s);
}

/**
 * Initializes GA4 (gtag.js) for this SPA.
 * Safe to call multiple times.
 */
export function initAnalytics(): void {
  if (initialized) return;
  initialized = true;

  if (typeof window === 'undefined') return;
  if (!measurementId) {
    // Useful for diagnosing missing envs on deployments.
    console.info('[GA] init skipped: missing VITE_GA_MEASUREMENT_ID');
    return;
  }

  ensureGtagStub();
  injectGtagScript(measurementId);

  console.info('[GA] init', { measurementId });

  // Basic GA init
  window.gtag?.('js', new Date());
  // SPA: page views are tracked on route changes
  window.gtag?.('config', measurementId, { send_page_view: false });
}

// NOTE: We do NOT have Clerk's userId readily available in this file.
// Call `setAnalyticsUserId()` from the app once auth is loaded.
let analyticsUserId: string | null = null;

export function setAnalyticsUserId(userId: string | null): void {
  analyticsUserId = userId;
}

function isAdminUser(): boolean {
  if (!adminUserId) return false;
  if (!analyticsUserId) return false;
  return analyticsUserId === adminUserId;
}

function getGtag(): GtagFunction | undefined {
  return typeof window !== 'undefined' ? window.gtag : undefined;
}

export function isAnalyticsEnabled(): boolean {
  return Boolean(measurementId && getGtag());
}

export function trackPageView(path: string): void {
  const gtag = getGtag();
  if (!measurementId || !gtag) return;
  if (isAdminUser()) return;

  gtag('event', 'page_view', {
    page_path: path,
  });
}

export function trackEvent(name: string, params?: Record<string, unknown>): void {
  const gtag = getGtag();
  if (!measurementId || !gtag) return;
  if (isAdminUser()) return;
  gtag('event', name, params);
}

export function trackPurchase(params: {
  transaction_id: string;
  value: number;
  currency: string;
}): void {
  trackEvent('purchase', params);
}
